import React, { useEffect, useState } from "react";
import { MessageSquare, Check, AlertCircle, Clock } from "lucide-react";
import useRequest from "../../../components/hook/use-req";

interface ApiAudience {
  audience_id: string;
  status: string;
  uploaded_by: string;
  created_at: string;
  metadata: {
    unique_count: number;
    duplicate_count: number;
    invalid_count: number;
    unique_file_url: string;
    unique_file_name: string;
  };
}

interface ApiAudienceResponse {
  org_id: string;
  audiences: ApiAudience[];
  limit: number;
  next_cursor?: number;
  previous_cursor?: number;
  total_count?: number;
}

const Contact: React.FC = () => {
  const token = localStorage.getItem("authToken");
  const [existingContact, setExistingContact] = useState<ApiAudience[]>([]);
  const [sortBy, setSortBy] = useState<keyof ApiAudience | "unique_count">(
    "created_at"
  );
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [currentCursor, setCurrentCursor] = useState<number | null>(null);
  const [nextCursor, setNextCursor] = useState<number | null>(null);
  const [previousCursors, setPreviousCursors] = useState<(number | null)[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const { makeRequest: getExistingData } = useRequest(`/audience/page`, "GET", {
    Authorization: `Bearer ${token}`,
  });

  const fetchData = async (cursorValue: number | null = null) => {
    const params: any = {
      limit: itemsPerPage,
    };

    // Only add cursor if it's not null
    if (cursorValue !== null) {
      params.cursor = cursorValue;
    }

    const [response] = await getExistingData(undefined, params);

    if (response && response.audiences) {
      // Sort audiences by created_at date in descending order (newest first)
      const sortedAudiences = response.audiences.sort(
        (a: ApiAudience, b: ApiAudience) => {
          return (
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
          );
        }
      );
      setExistingContact(sortedAudiences);

      setNextCursor(response.next_cursor || null);
      setCurrentCursor(cursorValue);
    } else {
    }
  };

  useEffect(() => {
    fetchData(null); // Initial load with no cursor
  }, []);

  // Function to get status color and icon
  const getStatusDetails = (status: string) => {
    switch (status.toUpperCase()) {
      case "READY":
        return {
          color: "bg-green-100 dark:bg-green-500/15 text-green-800 dark:text-green-400",
          icon: <Check size={16} />,
        };
      case "PROCESSING":
        return {
          color: "bg-yellow-100 dark:bg-yellow-500/15 text-yellow-800 dark:text-yellow-400",
          icon: <Clock size={16} />,
        };
      case "FAILED":
        return {
          color: "bg-red-100 dark:bg-red-500/15 text-red-800 dark:text-red-400",
          icon: <AlertCircle size={16} />,
        };
      default:
        return {
          color: "bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-300",
          icon: <MessageSquare size={16} />,
        };
    }
  };

  // Sorting function
  const handleSort = (key: keyof ApiAudience | "unique_count") => {
    const newSortOrder = sortBy === key && sortOrder === "asc" ? "desc" : "asc";
    setSortBy(key);
    setSortOrder(newSortOrder);

    const sorted = [...existingContact].sort((a, b) => {
      let valueA: string | number;
      let valueB: string | number;

      if (key === "unique_count") {
        valueA = a.metadata.unique_count;
        valueB = b.metadata.unique_count;
      } else if (key === "created_at") {
        valueA = new Date(a[key]).getTime();
        valueB = new Date(b[key]).getTime();
      } else {
        valueA = a[key] as string | number;
        valueB = b[key] as string | number;
      }

      if (typeof valueA === "string" && typeof valueB === "string") {
        valueA = valueA.toLowerCase();
        valueB = valueB.toLowerCase();
      }

      if (valueA < valueB) return newSortOrder === "asc" ? -1 : 1;
      if (valueA > valueB) return newSortOrder === "asc" ? 1 : -1;
      return 0;
    });

    setExistingContact(sorted);
  };

  // Handle next page
  const handleNextPage = async () => {
    if (nextCursor) {
      // Save current cursor to history before moving forward
      setPreviousCursors([...previousCursors, currentCursor]);
      setCurrentPage(currentPage + 1);
      await fetchData(nextCursor);
    }
  };

  // Handle previous page
  const handlePreviousPage = async () => {
    if (previousCursors.length > 0) {
      // Get the last cursor from history
      const newPreviousCursors = [...previousCursors];
      const lastCursor = newPreviousCursors.pop();

      setPreviousCursors(newPreviousCursors);
      setCurrentPage(currentPage - 1);
      await fetchData(lastCursor || null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 shadow-sm border-b dark:border-gray-700 rounded-xl mb-6">
        <div className="px-6 py-4">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Contact Audiences
          </h1>
          <p className="text-gray-600 dark:text-gray-300">
            Manage and view your uploaded contact audiences
          </p>
          {/* {totalCount > 0 && (
            <p className="text-sm text-gray-500 mt-1">
              Showing page {currentPage} of {totalPages} •{" "}
              {totalCount.toLocaleString()} total audiences
            </p>
          )} */}
        </div>
      </header>

      {/* Audiences Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  File Name
                </th>

                <th
                  className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort("unique_count")}
                >
                  Unique Contacts{" "}
                  {sortBy === "unique_count" &&
                    (sortOrder === "asc" ? "↑" : "↓")}
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Duplicate Contacts
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Invalid Contacts
                </th>
                <th
                  className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort("status")}
                >
                  Status{" "}
                  {sortBy === "status" && (sortOrder === "asc" ? "↑" : "↓")}
                </th>
                <th
                  className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort("created_at")}
                >
                  Created At{" "}
                  {sortBy === "created_at" && (sortOrder === "asc" ? "↑" : "↓")}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {existingContact.length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    className="px-6 py-4 text-center text-gray-500 dark:text-gray-400"
                  >
                    No audiences found
                  </td>
                </tr>
              ) : (
                existingContact.map((audience) => (
                  <tr
                    key={audience.audience_id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    <td className="px-6 capitalize py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-gray-100 truncate max-w-xs">
                        {audience.metadata.unique_file_name}
                      </div>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-gray-100">
                        {audience.metadata.unique_count.toLocaleString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-gray-100">
                        {audience.metadata.duplicate_count.toLocaleString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-gray-100">
                        {audience.metadata.invalid_count.toLocaleString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${
                          getStatusDetails(audience.status).color
                        }`}
                      >
                        {getStatusDetails(audience.status).icon}
                        <span className="ml-1">{audience.status}</span>
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-gray-100">
                        {new Date(audience.created_at).toLocaleDateString()}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {new Date(audience.created_at).toLocaleTimeString()}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}

        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
          {/* <Pagination
            currentPage={currentPage}
            hasNextPage={!!nextCursor}
            hasPrevPage={currentPage > 1}
            onNextPage={handleNextPage}
            onPrevPage={handlePreviousPage}
          /> */}
        </div>
      </div>
    </div>
  );
};

export default Contact;
